/**
 * Reusable prospect events linear chart component for teams and streams
 */

import React, { useMemo } from 'react';
import { Box, CircularProgress, Tabs, Tab } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import ReactECharts from 'echarts-for-react';
import { teamApi } from '@/api/team';

export interface ProspectEventsLinearChartProps {
  teamUuid?: string;
  streamUuid?: string;
  defaultRange?: '7d' | '30d' | '90d' | 'ytd';
  height?: number;
  showRangeTabs?: boolean;
  onRangeChange?: (range: '7d' | '30d' | '90d' | 'ytd') => void;
  enabled?: boolean;
}

export const ProspectEventsLinearChart: React.FC<ProspectEventsLinearChartProps> = ({
  teamUuid,
  streamUuid,
  defaultRange = '30d',
  height = 300,
  showRangeTabs = true,
  onRangeChange,
  enabled = true,
}) => {
  const [rangePreset, setRangePreset] = React.useState<'7d' | '30d' | '90d' | 'ytd'>(defaultRange);

  // Compute date range (UTC) from preset
  const { date_from, date_to } = useMemo(() => {
    const now = new Date();
    const end = new Date(now);
    let start = new Date(now);
    switch (rangePreset) {
      case '7d':
        start.setUTCDate(start.getUTCDate() - 7);
        break;
      case '90d':
        start.setUTCDate(start.getUTCDate() - 90);
        break;
      case 'ytd':
        start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1, 0, 0, 0));
        break;
      case '30d':
      default:
        start.setUTCDate(start.getUTCDate() - 30);
    }
    return { date_from: start.toISOString(), date_to: end.toISOString() };
  }, [rangePreset]);

  // Fetch analytics rows
  const { data: eventsRows, isLoading: isLoadingEvents } = useQuery({
    queryKey: ['prospect-events', teamUuid, streamUuid, date_from, date_to],
    queryFn: async () => {
      if (teamUuid) {
        return teamApi.getTeamProspectEvents(teamUuid, { date_from, date_to });
      } else if (streamUuid) {
        return teamApi.getStreamProspectEvents(streamUuid, { date_from, date_to });
      }
      return [];
    },
    enabled: enabled && (!!teamUuid || !!streamUuid),
  });

  // Build ECharts option
  const chartOption = useMemo(() => {
    const rows = eventsRows || [];
    const actions = Array.from(new Set(rows.map(r => r.action)));
    const series = actions.map(action => ({
      type: 'line',
      name: action,
      data: rows.filter(r => r.action === action).map(r => [new Date(r.time + 'T00:00:00Z').getTime(), r.events]),
      showSymbol: false,
      sampling: 'lttb',
      smooth: 0.15,
      lineStyle: { width: 2 },
      areaStyle: { opacity: 0.06 },
    }));
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis' as const,
        formatter: (params: any) => {
          const first = Array.isArray(params) ? params[0] : params;
          const d = new Date(first.axisValue);
          const dateStr = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10);
          const lines = (Array.isArray(params) ? params : [params]).map((p: any) => `${p.marker}${p.seriesName}: ${p.data[1]}`);
          return `${dateStr}<br/>${lines.join('<br/>')}`;
        },
      },
      legend: { top: 0 },
      grid: { left: 40, right: 20, top: 40, bottom: 40 },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          formatter: (value: number) => {
            const d = new Date(value);
            return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10);
          },
        },
      },
      yAxis: { type: 'value' },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 20 }],
      series,
    };
  }, [eventsRows]);

  const handleRangeChange = (newRange: '7d' | '30d' | '90d' | 'ytd') => {
    setRangePreset(newRange);
    onRangeChange?.(newRange);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showRangeTabs && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tabs value={rangePreset} onChange={(_, v) => handleRangeChange(v)}>
            <Tab value="7d" label="7d" />
            <Tab value="30d" label="30d" />
            <Tab value="90d" label="90d" />
            <Tab value="ytd" label="YTD" />
          </Tabs>
        </Box>
      )}
      <Box sx={{ height }}>
        {isLoadingEvents ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <ReactECharts option={chartOption as any} style={{ height: '100%', width: '100%' }} />
        )}
      </Box>
    </Box>
  );
};
